import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CircleNotch, CheckCircle, Truck } from "@phosphor-icons/react";
import { requestLoad } from "@/lib/api";

export const RequestLoadDialog = ({ open, onOpenChange, load }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.carrier_name || !form.email) {
      toast.error("Carrier name and email are required");
      return;
    }
    setLoading(true);
    try {
      await requestLoad({
        load_id: load.id,
        carrier_name: form.carrier_name,
        mc_number: form.mc_number || "",
        email: form.email,
        phone: form.phone || "",
        message: form.message || "",
      });
      setDone(true);
      toast.success("Request sent — we'll be in touch!");
    } catch {
      toast.error("Could not send request. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({}); }, 200);
  };

  if (!load) return null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#111A2E] border-white/10 text-white max-w-lg" data-testid="request-load-dialog">
        {done ? (
          <div className="py-10 text-center flex flex-col items-center gap-4" data-testid="request-success">
            <CheckCircle size={56} weight="fill" className="text-[#2563EB]" />
            <h3 className="font-display text-2xl font-bold">Request received</h3>
            <p className="text-[#94A3B8] max-w-sm">Coastal dispatch is reviewing your interest on this lane. We'll be in touch shortly.</p>
            <button onClick={() => close(false)} className="mt-2 px-6 py-2.5 rounded-full bg-[#2563EB] text-white font-semibold" data-testid="request-close-btn">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
                <Truck size={24} className="text-[#2563EB]" /> Request Load
              </DialogTitle>
              <DialogDescription className="text-[#94A3B8]">
                {load.origin_city}, {load.origin_state} → {load.dest_city}, {load.dest_state} ·{" "}
                <span className="text-[#10B981] font-semibold">${load.rate_usd.toLocaleString()}</span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Carrier Name *"><Input data-testid="req-carrier-name" value={form.carrier_name || ""} onChange={(e) => set("carrier_name", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
                <Field label="MC Number"><Input data-testid="req-mc" value={form.mc_number || ""} onChange={(e) => set("mc_number", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email *"><Input type="email" data-testid="req-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
                <Field label="Phone"><Input data-testid="req-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-[#0B1220] border-white/10" /></Field>
              </div>
              <Field label="Message"><Textarea data-testid="req-message" value={form.message || ""} onChange={(e) => set("message", e.target.value)} className="bg-[#0B1220] border-white/10" rows={3} placeholder="Availability, rate expectations, etc." /></Field>
              <button type="submit" disabled={loading} data-testid="req-submit-btn" className="w-full py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <CircleNotch size={18} className="animate-spin" />} Send Request
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[#94A3B8]">{label}</Label>
    {children}
  </div>
);
